// src/pages/admin/Clients.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from 'contexts/AppContext';
import { usersService } from 'infrastructure/firebase';
import { Card, Button, Avatar, Badge, EmptyState } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';

export default function AdminClients() {
  const isMobile = useMobile();
  const { estabClients: clients, appointments, showToast } = useApp();
  const navigate = useNavigate();
  const [search, setSearch]       = useState('');
  const [expanded, setExpanded]   = useState(null); // clientId cujo histórico está aberto
  const [editing, setEditing]     = useState(null); // { id, name, phone }
  const [savingEdit, setSavingEdit] = useState(false);

  const handleEditSave = async (c) => {
    if (!editing?.name?.trim()) { showToast('Nome não pode ser vazio.', 'warning'); return; }
    setSavingEdit(true);
    try {
      await usersService.update(c.id, { name: editing.name.trim(), phone: editing.phone?.trim() || '' });
      showToast('Cliente atualizado!');
      setEditing(null);
    } catch { showToast('Erro ao atualizar cliente.', 'danger'); }
    finally { setSavingEdit(false); }
  };

  const filtered = clients.filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{ display:'flex',gap:10,marginBottom:20,alignItems:'center',flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:1,maxWidth:300 }}>
          <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#a8a49d' }}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar cliente..."
            style={{ width:'100%',padding:'8px 14px 8px 36px',border:'1.5px solid #e5e2da',borderRadius:10,fontSize:13,outline:'none',boxSizing:'border-box' }} />
        </div>
        <Button variant="primary" onClick={()=>navigate('/admin/users')}>+ Cadastrar Cliente</Button>
      </div>

      {/* Modal edição rápida de cliente */}
      {editing && (
        <>
          <div onClick={() => setEditing(null)} style={{ position:'fixed',inset:0,background:'rgba(61,52,71,0.32)',backdropFilter:'blur(4px)',zIndex:200 }} />
          <div style={{ position:'fixed',top:'50%',left:'50%',transform:'translate(-50%,-50%)',background:'#fff',borderRadius:16,padding:'28px 28px 24px',width:'min(420px,calc(100vw - 32px))',zIndex:201,boxShadow:'0 12px 40px rgba(74,63,88,0.18)',fontFamily:"'DM Sans',sans-serif" }}>
            <div style={{ fontFamily:"'Instrument Serif',serif",fontSize:22,color:'#3D3447',marginBottom:16 }}>Editar cliente</div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block',fontSize:13,fontWeight:500,color:'#6B6070',marginBottom:6 }}>Nome *</label>
              <input value={editing.name} onChange={e=>setEditing(ed=>({...ed,name:e.target.value}))}
                style={{ width:'100%',padding:'11px 14px',border:'1.5px solid #E8E5EC',borderRadius:10,fontSize:14,outline:'none',boxSizing:'border-box' }} />
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block',fontSize:13,fontWeight:500,color:'#6B6070',marginBottom:6 }}>Telefone</label>
              <input value={editing.phone} onChange={e=>setEditing(ed=>({...ed,phone:e.target.value}))}
                placeholder="(11) 99999-9999"
                style={{ width:'100%',padding:'11px 14px',border:'1.5px solid #E8E5EC',borderRadius:10,fontSize:14,outline:'none',boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
              <Button variant="secondary" onClick={()=>setEditing(null)}>Cancelar</Button>
              <Button variant="primary" loading={savingEdit} disabled={savingEdit} onClick={()=>handleEditSave(editing)}>Salvar</Button>
            </div>
          </div>
        </>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon="👥" title="Nenhum cliente cadastrado" action={<Button variant="primary" onClick={()=>navigate('/admin/users')}>+ Cadastrar</Button>} />
      ) : isMobile ? (
        /* ── Mobile: cards ──────────────────────────────────── */
        <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
          {filtered.map(c => {
            const cAppts = appointments.filter(a => a.clientId === c.id);
            return (
              <Card key={c.id}>
                <div style={{ padding:'14px 16px' }}>
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <Avatar name={c.name||'?'} size="sm" />
                      <strong style={{ fontSize:14 }}>{c.name}</strong>
                    </div>
                    <Badge variant="info">{cAppts.length} atend.</Badge>
                  </div>
                  {c.phone && <div style={{ fontSize:12,color:'#6b6760',marginBottom:2 }}>📞 {c.phone}</div>}
                  <div style={{ fontSize:12,color:'#6b6760',marginBottom:8 }}>✉️ {c.email}</div>
                  <div style={{ display:'flex',gap:8 }}>
                    <Button variant="ghost" size="sm" onClick={() => setExpanded(expanded===c.id?null:c.id)}>
                      {expanded===c.id ? '▲ Fechar' : '👁️ Histórico'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditing({id:c.id,name:c.name,phone:c.phone||''})}>✏️ Editar</Button>
                  </div>
                  {expanded===c.id && (
                    <div style={{ marginTop:10,padding:'10px 0 0',borderTop:'1px solid #e5e2da' }}>
                      {cAppts.length===0 ? <div style={{ fontSize:12,color:'#a8a49d' }}>Sem agendamentos.</div> : (
                        [...cAppts].sort((a,b)=>(b.date||'').localeCompare(a.date||'')).slice(0,3).map(ap=>(
                          <div key={ap.id} style={{ fontSize:12,color:'#3D3447',padding:'4px 0',borderBottom:'1px solid #f0eee9' }}>
                            {ap.date} {ap.time} — {ap.procedureName}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        /* ── Desktop: tabela ─────────────────────────────────── */
        <Card>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
              <thead>
                <tr>
                  {['Cliente','Telefone','E-mail','Agendamentos','Último atend.','Ações'].map((h,i)=>(
                    <th key={i} style={{ textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:1,color:'#a8a49d',borderBottom:'1px solid #e5e2da',background:'#f8f7f5' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                const cAppts = appointments.filter(a => a.clientId === c.id);
                const last = cAppts.reduce((best, a) => {
                  const dt = a.dateTime?.toDate?.() || (a.date ? new Date(`${a.date}T${a.time || '00:00'}:00`) : null);
                  if (!dt || isNaN(dt.getTime())) return best;
                  if (!best || dt > best.dt) return { appt: a, dt };
                  return best;
                }, null);
                const lastDt = last?.dt || null;
                // Fragment necessário para retornar dois <tr> do mesmo map
                return (
                  <React.Fragment key={c.id}>
                    <tr>
                      <td style={{ padding:'13px 16px',borderBottom: expanded===c.id ? 'none' : '1px solid #e5e2da' }}>
                        <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                          <Avatar name={c.name||'?'} size="sm" />
                          <strong>{c.name}</strong>
                        </div>
                      </td>
                      <td style={{ padding:'13px 16px',borderBottom: expanded===c.id ? 'none' : '1px solid #e5e2da',color:'#6b6760' }}>{c.phone || '—'}</td>
                      <td style={{ padding:'13px 16px',borderBottom: expanded===c.id ? 'none' : '1px solid #e5e2da',color:'#6b6760' }}>{c.email}</td>
                      <td style={{ padding:'13px 16px',borderBottom: expanded===c.id ? 'none' : '1px solid #e5e2da' }}>
                        <Badge variant="info">{cAppts.length} atend.</Badge>
                      </td>
                      <td style={{ padding:'13px 16px',borderBottom: expanded===c.id ? 'none' : '1px solid #e5e2da',color:'#a8a49d' }}>
                        {lastDt ? lastDt.toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td style={{ padding:'13px 16px',borderBottom: expanded===c.id ? 'none' : '1px solid #e5e2da' }}>
                        <div style={{ display:'flex',gap:4 }}>
                          <Button variant="ghost" size="sm" title="Ver agendamentos"
                            onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                            {expanded === c.id ? '▲' : '👁️'}
                          </Button>
                          <Button variant="ghost" size="sm" title="Editar"
                            onClick={() => setEditing({ id: c.id, name: c.name, phone: c.phone || '' })}>
                            ✏️
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expanded === c.id && (
                      <tr>
                        <td colSpan={6} style={{ padding:'0 16px 14px', borderBottom:'1px solid #e5e2da', background:'#faf8fc' }}>
                          <div style={{ padding:'12px 0 4px', fontSize:12, fontWeight:600, color:'#a8a49d', textTransform:'uppercase', letterSpacing:1 }}>
                            Histórico de agendamentos
                          </div>
                          {cAppts.length === 0 ? (
                            <div style={{ fontSize:13, color:'#a8a49d', padding:'8px 0' }}>Nenhum agendamento registrado.</div>
                          ) : (
                            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                              {[...cAppts].sort((a,b)=>(b.date||'').localeCompare(a.date||'')).slice(0,5).map(ap => (
                                <div key={ap.id} style={{ display:'flex', gap:12, alignItems:'center', fontSize:12, color:'#3D3447' }}>
                                  <span style={{ minWidth:80, color:'#6b6760' }}>{ap.date ? new Date(`${ap.date}T${ap.time||'00:00'}:00`).toLocaleDateString('pt-BR') : '—'}</span>
                                  <span style={{ minWidth:40, color:'#a8a49d' }}>{ap.time || ''}</span>
                                  <span style={{ flex:1 }}>{ap.procedureName}</span>
                                  <span style={{ color:'#a8a49d' }}>{ap.professionalName}</span>
                                  <Badge variant={ap.status==='confirmed'?'success':ap.status==='cancelled'?'danger':'neutral'}>
                                    {ap.status==='confirmed'?'Confirmado':ap.status==='cancelled'?'Cancelado':ap.status==='waitingConfirm'?'Aguardando':'Pendente'}
                                  </Badge>
                                </div>
                              ))}
                              {cAppts.length > 5 && <div style={{ fontSize:11, color:'#a8a49d', paddingTop:4 }}>+{cAppts.length-5} mais...</div>}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </div>
  );
}
